import { useCallback, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import BaseInputField from "../../../../components/elements/BaseInputField"
import BaseModal from "../../../../components/layout/BaseModal"
import { ReactComponent as CLOSE } from "../../../../assets/CloseICON.svg"
import { ReactComponent as CALENDAR } from "../../../../assets/CalendarICON.svg"
import { ReactComponent as REFRESH } from "../../../../assets/RefreshICON.svg"

type RefreshStatisticsModalProps = {
    setStartDate: (date: string) => void
    setEndDate: (date: string) => void
    startDate: string
    endDate: string
    modalId: number
    handleRefresh: (start: string, end: string) => void
    modalClose: () => void
}

type PeriodKey = "currentMonth" | "lastMonth" | "currentQuarter" | "lastQuarter" | "currentYear" | "lastYear" | "lastTwoYears" | "lastThreeYears"
type Selection = PeriodKey | "custom"
type Period = { key: PeriodKey; label: string; start: string; end: string }

const DATE_FORMAT = "YYYY-MM-DD"

const buildPeriods = (today: Dayjs): Period[] => {
    const quarterStart = today.startOf("year").add(Math.floor(today.month() / 3) * 3, "month")
    const lastQuarterStart = quarterStart.subtract(3, "month")
    const monthStart = today.startOf("month")
    const yearStart = today.startOf("year")
    const period = (key: PeriodKey, label: string, start: Dayjs, end: Dayjs): Period => ({
        key,
        label,
        start: start.format(DATE_FORMAT),
        end: end.format(DATE_FORMAT),
    })
    return [
        period("currentMonth", "Aktueller Monat", monthStart, today),
        period("lastMonth", "Letzter Monat", monthStart.subtract(1, "month"), monthStart.subtract(1, "day")),
        period("currentQuarter", "Aktuelles Quartal", quarterStart, today),
        period("lastQuarter", "Letztes Quartal", lastQuarterStart, quarterStart.subtract(1, "day")),
        period("currentYear", "Aktuelles Jahr", yearStart, today),
        period("lastYear", "Letztes Jahr", yearStart.subtract(1, "year"), yearStart.subtract(1, "day")),
        period("lastTwoYears", "Letzte 2 Jahre", yearStart.subtract(1, "year"), today),
        period("lastThreeYears", "Letzte 3 Jahre", yearStart.subtract(2, "year"), today),
    ]
}

const formatShort = (date: string) => dayjs(date).format("DD.MM.YY")

const tileBase = "flex flex-col justify-center min-h-16 px-4 py-2 rounded-default border text-left transition-colors"
const tileActive = "bg-primary border-primary text-white"
const tileInactive = "bg-gray-50 border-gray-200 hover:bg-gray-100"

export default function RefreshStatisticsModal(props: RefreshStatisticsModalProps) {
    const [periods, setPeriods] = useState<Period[]>(() => buildPeriods(dayjs()))
    const [selection, setSelection] = useState<Selection>("custom")
    const [customStart, setCustomStart] = useState<string>(props.startDate)
    const [customEnd, setCustomEnd] = useState<string>(props.endDate)
    const [error, setError] = useState<string>("")
    const [displayWarning, setDisplayWarning] = useState<boolean>(false)

    const { startDate, endDate } = props
    const syncWithDashboard = useCallback(() => {
        const nextPeriods = buildPeriods(dayjs())
        const match = nextPeriods.find((p) => p.start === startDate && p.end === endDate)
        setPeriods(nextPeriods)
        setSelection(match ? match.key : "custom")
        setCustomStart(startDate)
        setCustomEnd(endDate)
        setError("")
        setDisplayWarning(false)
    }, [startDate, endDate])

    const activePeriod = periods.find((p) => p.key === selection)
    const range = activePeriod ? { start: activePeriod.start, end: activePeriod.end } : { start: customStart, end: customEnd }

    const validate = () => {
        if (!range.start || !range.end) {
            setError("Bitte Start- und Enddatum angeben.")
            return false
        }
        if (dayjs(range.start).isAfter(dayjs(range.end))) {
            setError("Das Startdatum darf nicht nach dem Enddatum liegen.")
            return false
        }
        setError("")
        return true
    }

    const selectPeriod = (key: PeriodKey) => {
        setSelection(key)
        setError("")
    }

    const selectCustom = () => {
        if (selection === "custom") return
        setCustomStart(range.start)
        setCustomEnd(range.end)
        setSelection("custom")
        setError("")
    }

    const onApply = () => {
        if (!validate()) return
        props.setStartDate(range.start)
        props.setEndDate(range.end)
        props.modalClose()
    }

    const onConfirmRefresh = () => {
        if (!validate()) {
            setDisplayWarning(false)
            return
        }
        props.handleRefresh(range.start, range.end)
        props.modalClose()
    }

    return (
        <BaseModal
            modalId={props.modalId}
            size="fit"
            title="Zeitraum"
            onOpen={syncWithDashboard}
            bottomRow={
                <div className="w-full md:w-[32rem] pb-2">
                    {displayWarning && (
                        <p className="text-gray-400 text-xs mb-2">
                            <strong>Warnung:</strong> Diese Aktion kann ggf. einige Minuten dauern. Führen Sie diese Aktion nur, wenn Sie die Statistiken
                            aktualisieren möchten.
                        </p>
                    )}
                    <div className="flex flex-row gap-2">
                        {displayWarning ? (
                            <>
                                <button className="btn btn-outline w-fit" type="button" onClick={() => setDisplayWarning(false)}>
                                    <CLOSE className="w-6 h-6" />
                                </button>
                                <button className="btn btn-primary grow" type="button" onClick={onConfirmRefresh}>
                                    Bestätigen
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn btn-outline"
                                    type="button"
                                    onClick={() => {
                                        if (validate()) setDisplayWarning(true)
                                    }}>
                                    <REFRESH className="w-6 h-6" />
                                    Neu laden
                                </button>
                                <button className="btn btn-primary grow" type="button" onClick={onApply}>
                                    Abfragen
                                </button>
                            </>
                        )}
                    </div>
                </div>
            }>
            <div className="w-full md:w-[32rem] pt-4 pb-2 pr-2 flex flex-col gap-2">
                <div className="grid grid-cols-2 gap-2">
                    {periods.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            className={`${tileBase} ${selection === p.key ? tileActive : tileInactive}`}
                            onClick={() => selectPeriod(p.key)}>
                            <span className="font-bold text-sm">{p.label}</span>
                            <span className={`text-xs ${selection === p.key ? "text-white/80" : "text-gray-500"}`}>
                                {formatShort(p.start)} – {formatShort(p.end)}
                            </span>
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    className={`${tileBase} w-full flex-row items-center gap-3 border-dashed ${selection === "custom" ? tileActive : tileInactive}`}
                    onClick={selectCustom}>
                    <CALENDAR className="w-6 h-6 shrink-0" />
                    <span className="font-bold text-sm">Benutzerdefiniert …</span>
                </button>
                {selection === "custom" && (
                    <div className="flex flex-col sm:flex-row gap-2 pt-1">
                        <BaseInputField
                            label="Startdatum"
                            type="date"
                            value={customStart}
                            status={error ? "error" : undefined}
                            onChange={(value) => {
                                setCustomStart(value)
                                setError("")
                            }}
                        />
                        <BaseInputField
                            label="Enddatum"
                            type="date"
                            value={customEnd}
                            status={error ? "error" : undefined}
                            onChange={(value) => {
                                setCustomEnd(value)
                                setError("")
                            }}
                        />
                    </div>
                )}
                {error && <p className="text-red-500 text-sm pl-2">{error}</p>}
            </div>
        </BaseModal>
    )
}
